'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Loader2, Inbox, ChevronRight, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import Link from 'next/link';

interface EngagementRow {
  id: string;
  status: string;
  engagement_type: string | null;
  buyer_question_experience: string | null;
  buyer_question_relevance: string | null;
  buyer_scope_note: string | null;
  created_at: string;
  has_qa_response: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-400/15 text-amber-400 border-amber-400/30',
  ACCEPTED: 'bg-primary/10 text-primary border-primary/30',
  IN_PROGRESS: 'bg-secondary/10 text-secondary border-secondary/30',
  COMPLETED: 'bg-green-500/10 text-green-400 border-green-500/30',
  DECLINED: 'bg-red-500/10 text-red-400 border-red-500/30',
  CANCELLED: 'bg-muted text-muted-foreground border-border',
};

export default function ExpertEngagementsContent() {
  const supabase = createClient();
  const [engagements, setEngagements] = useState<EngagementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEngagements = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: ep } = await supabase
        .from('expert_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!ep) { setLoading(false); return; }

      const { data: engs, error: engErr } = await supabase
        .from('engagements')
        .select('id, status, engagement_type, buyer_question_experience, buyer_question_relevance, buyer_scope_note, created_at')
        .eq('expert_id', ep.id)
        .order('created_at', { ascending: false });

      if (engErr) { setError(engErr.message); setLoading(false); return; }

      if (engs) {
        const enriched = await Promise.all(
          engs.map(async (eng: any) => {
            const { data: qa } = await supabase
              .from('engagement_qa_responses')
              .select('id')
              .eq('engagement_id', eng.id)
              .maybeSingle();

            return { ...eng, has_qa_response: !!qa };
          })
        );
        setEngagements(enriched);
      }
      setLoading(false);
    };
    fetchEngagements();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 size={24} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="py-8 max-w-3xl mx-auto px-4">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Inbox size={16} className="text-primary" />
        </div>
        <h1 className="font-heading text-display text-foreground">Incoming Enquiries</h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {engagements.length === 0 ? (
        <div className="surface-elevated p-12 text-center">
          <Inbox size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">No enquiries yet</p>
          <p className="text-sm text-muted-foreground">When buyers send you enquiries, they will appear here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {engagements.map((eng) => (
            <div key={eng.id} className="surface-elevated overflow-hidden">
              <div
                className="p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={() => setExpandedId(expandedId === eng.id ? null : eng.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {eng.engagement_type && (
                      <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded"
                        style={{ background: '#EEF2FF', color: '#4338CA' }}>
                        {eng.engagement_type}
                      </span>
                    )}
                    {eng.status === 'PENDING' && !eng.has_qa_response && (
                      <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-400 border border-amber-400/30">
                        Awaiting your response
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground font-mono-data">
                    {new Date(eng.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono-data text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[eng.status] || STATUS_COLORS.PENDING}`}>
                    {eng.status.replace('_', ' ')}
                  </span>
                  {expandedId === eng.id ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
                </div>
              </div>

              {expandedId === eng.id && (
                <div className="px-5 pb-5 border-t border-border/50 pt-4 flex flex-col gap-4">
                  {eng.buyer_question_experience && (
                    <div>
                      <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">Buyer's Ask</p>
                      <p className="text-sm text-foreground leading-relaxed italic border-l-2 border-primary/40 pl-3">
                        {eng.buyer_question_experience}
                      </p>
                    </div>
                  )}
                  {eng.buyer_scope_note && (
                    <div>
                      <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">Additional Context</p>
                      <p className="text-sm text-muted-foreground">{eng.buyer_scope_note}</p>
                    </div>
                  )}

                  {eng.status === 'PENDING' && !eng.has_qa_response && (
                    <Link
                      href={`/expert/respond/${eng.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all w-fit"
                    >
                      Respond to This Enquiry
                      <ChevronRight size={14} />
                    </Link>
                  )}

                  {eng.has_qa_response && (
                    <p className="text-sm text-secondary font-medium">✓ Response submitted</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
